import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteNavBar from '../navigation/SiteNavBar';
import Footer from '../footer/Footer';
import CopyRight from '../footer/CopyRight';
import '../../../assets/css/main.css'
import '../../../assets/bootstrap/css/bootstrap.min.css'
import workprogress from '../../../images/work-inprogress.jpg'
import { useCart } from '../../../context/CartContext';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { config } from '../../../config';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';

function confirmRemoveFromCartLine(itemTitle) {
  const label = itemTitle ? `"${itemTitle}"` : "this item";
  return window.confirm(
    `Are you sure you want to remove ${label} from your cart?`
  );
}

function asClientId(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    return String(value._id || value.id || '');
  }
  const s = String(value);
  return s === '[object Object]' ? '' : s;
}

function formatCents(cents) {
  return (Number(cents) / 100).toFixed(2);
}

function cartChargeItems(cart) {
  return cart.map((item) => ({
    productId: asClientId(item.productId || item._id || item.id),
    quantity: item.quantity || 1,
  }));
}

function cartQuoteKey(items) {
  return items.map((item) => `${item.productId}:${item.quantity}`).join('|');
}

function quoteForItems(data, items) {
  return { ...data, cartKey: cartQuoteKey(items) };
}

const Cart = () => {
  const { cart, removeFromCart, clearCart, incrementQuantity, decrementQuantity, applyPeriodId } = useCart();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [feeConfig, setFeeConfig] = useState({ platformFeePct: 5, organizerFeePct: 10, pickupFee: 0.01 });

  const marketplaceFeePct = (feeConfig.platformFeePct || 0) + (feeConfig.organizerFeePct || 0);

  const producerUnitPrice = (item) => {
    const explicit = Number(item.price);
    if (explicit > 0) return explicit;
    const cost = Number(item.cost) || 0;
    if (cost > 0 && marketplaceFeePct > 0) {
      return cost / (1 + marketplaceFeePct / 100);
    }
    return 0;
  };

  // Server quote from the same pricing Stripe charges. Product cost already includes the
  // marketplace fee; the quote adds only the pickup fee.
  const [priceQuote, setPriceQuote] = useState(null);
  const [pricingPending, setPricingPending] = useState(false);
  const [pricingError, setPricingError] = useState('');
  const currentQuoteKey = cartQuoteKey(cartChargeItems(cart));
  const quoteIsCurrent = priceQuote?.cartKey === currentQuoteKey;
  const chargeCents = quoteIsCurrent && Number.isInteger(priceQuote?.totalCents) ? priceQuote.totalCents : null;
  const chargeReady = chargeCents != null && !pricingPending && !pricingError;

  // Form state
  const [pickupType, setPickupType] = useState('Pickup');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [address, setAddress] = useState('');
  const [periodInfo, setPeriodInfo] = useState(null);
  const [distributionError, setDistributionError] = useState('');

  const isDistributionComplete = Boolean(location.trim() && address.trim());

  const validateDistribution = () => {
    if (!location.trim()) {
      const message =
        pickupType === 'Pickup'
          ? 'Please select a pickup location.'
          : 'Please select a delivery location.';
      setDistributionError(message);
      return false;
    }
    if (!address.trim()) {
      const message =
        pickupType === 'Pickup'
          ? 'Pickup address is required.'
          : 'Please enter your delivery address.';
      setDistributionError(message);
      return false;
    }
    setDistributionError('');
    return true;
  };

  // Stripe state
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [intentLoading, setIntentLoading] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState(null);

  // Order Info
  const [systemOrderId, setSystemOrderId] = useState(null);

  // Saved payment methods state
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);

  // Prevent double API calls
  const intentCreatedRef = useRef(false);
  /** Reuse the same PENDING order when customer retries after a declined card. */
  const pendingCheckoutOrderRef = useRef(null);

  function orderRecordId(orderData) {
    return orderData?.id || orderData?._id || null;
  }

  async function resolveCheckoutOrder(paymentIntentId) {
    const storedId = pendingCheckoutOrderRef.current;
    if (storedId) {
      try {
        const existing = await axios.get(`${config.API_BASE}/api/orders/${storedId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (existing.data?.status === 'PENDING') {
          return existing;
        }
      } catch (err) {
        console.warn('Could not reuse pending checkout order, creating a new one', err);
      }
    }

    const orderInfoResponse = await placeOrder(paymentIntentId);
    const oid = orderRecordId(orderInfoResponse.data);
    pendingCheckoutOrderRef.current = oid;
    setSystemOrderId(oid);
    return orderInfoResponse;
  }

  // Pickup/Delivery location logic
  const pickupLocations = [
    {
      name: "Dubuque Farmer's Market",
      address: "101 W 11th St, Dubuque, IA 52001"
    },
    {
      name: "St Luke's Methodist Church",
      address: "1199 Main St, Dubuque, IA 52001"
    }
  ];
  const deliveryLocations = [
    { name: "Home" },
    { name: "Office" }
  ];

  // Fetch marketplace fee settings for cart display
  useEffect(() => {
    axios.get(`${config.API_BASE}/api/settings`)
      .then((response) => {
        setFeeConfig({
          platformFeePct: response.data?.platformFeePct ?? 5,
          organizerFeePct: response.data?.organizerFeePct ?? 10,
          pickupFee: response.data?.pickupFee ?? 0.01,
        });
      })
      .catch(() => {});
  }, []);

  // Initialize Stripe on mount
  useEffect(() => {
    if (config.STRIPE_PLATFORM_PUBLISHABLE_KEY) {
      setStripePromise(loadStripe(config.STRIPE_PLATFORM_PUBLISHABLE_KEY));
    }
  }, []);

  // Create payment intent once when user is available
  useEffect(() => {
    if (user && cart.length > 0 && !clientSecret && !intentLoading && !intentCreatedRef.current) {
      intentCreatedRef.current = true;
      handleProceedToPayment();
    }
  }, [user]);

  // Price the cart with the same function that sets the Stripe amount.
  useEffect(() => {
    if (cart.length === 0) {
      setPriceQuote(null);
      setPricingPending(false);
      setPricingError('');
      return;
    }

    const items = cartChargeItems(cart);
    if (items.some((item) => !item.productId)) {
      setPriceQuote(null);
      setPricingPending(false);
      setPricingError('A cart item is missing a product.');
      return;
    }

    let cancelled = false;
    setPricingPending(true);
    const timer = setTimeout(async () => {
      try {
        const res = paymentIntentId
          ? await axios.post(`${config.API_BASE}/api/payments/update-intent`, {
              items,
              customerId: user?._id,
              paymentIntentId,
              orderId: systemOrderId || pendingCheckoutOrderRef.current || '',
            })
          : await axios.post(`${config.API_BASE}/api/payments/quote`, { items });
        if (cancelled) return;
        setPriceQuote(quoteForItems(res.data, items));
        setPricingError('');
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to price cart', err);
        setPriceQuote(null);
        setPricingError(err.response?.data?.error || 'Could not calculate the order total.');
      } finally {
        if (!cancelled) setPricingPending(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cart, user?._id, paymentIntentId, systemOrderId]);

  // Update address field based on pickup/delivery selection
  useEffect(() => {
    if (pickupType === "Pickup") {
      const loc = pickupLocations.find(l => l.name === location);
      if (loc) setAddress(loc.address);
      else setAddress("");
    } else {
      setAddress("");
    }
  }, [pickupType, location]);

  // Fetch period information when component mounts
  useEffect(() => {
    const fetchPeriodInformation = async () => {
      try {
        const response = await axios.get(`${config.API_BASE}/api/orders/period`);
        
        if (response.data.distribution) {
          const today = new Date();
          const todayFormatted = today.toISOString().split('T')[0];
          setDate(todayFormatted);
          setPeriodInfo(response.data);
          if (response.data.periodId) {
            applyPeriodId(response.data.periodId);
          }
        }
      } catch (error) {
        console.error('Failed to fetch period information:', error);
        const today = new Date();
        const fallbackDate = today.toISOString().split('T')[0];
        setDate(fallbackDate);
      }
    };

    fetchPeriodInformation();
  }, []);

  // Fetch saved payment methods for the user
  useEffect(() => {
    const fetchSavedPaymentMethods = async () => {
      if (!user?._id || !token) {
        console.log('⚠ Cannot fetch payment methods: user not logged in');
        setLoadingPaymentMethods(false);
        return;
      }

      try {
        console.log('Fetching saved payment methods for user:', user._id);
        const response = await axios.get(
          `${config.API_BASE}/api/payment-methods-orders/${user._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log('✓ Payment methods fetched:', response.data);
        setSavedPaymentMethods(response.data.methods || []);
        setDefaultPaymentMethodId(response.data.defaultPaymentMethodId);

        if (response.data.methods && response.data.methods.length > 0) {
          console.log(`Found ${response.data.methods.length} saved payment method(s)`);
        } else {
          console.log('No saved payment methods found - user can save a card during checkout');
        }
      } catch (error) {
        console.error('✗ Error fetching saved payment methods:', error.response?.data || error.message);
        setSavedPaymentMethods([]);
      } finally {
        setLoadingPaymentMethods(false);
      }
    };

    fetchSavedPaymentMethods();
  }, [user, token]);

  const placeOrder = async (paymentIntentId) => {
    const periodId = periodInfo?.periodId || cart[0]?.periodId;
    const orderPayload = {
      customerId: user._id,
      items: cart.map(item => ({
        productId: asClientId(item.productId || item._id || item.id),
        quantity: item.quantity || 1,
        price: producerUnitPrice(item),
        producerId: asClientId(item.producer?._id || item.producerId || item.producer),
        periodId: asClientId(item.periodId || periodId)
      })),
      total: priceQuote?.amount,
      status: 'PENDING',
      producerId: cart[0]?.producerId,
      periodId,
      paymentIntentId
    };
    const orderId = await axios.post(`${config.API_BASE}/api/orders`, orderPayload, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return orderId;
  };

  const createOrder = async (paymentIntentId) => resolveCheckoutOrder(paymentIntentId);

  const handleProceedToPayment = async () => {
    if (!user) {
      const shouldLogin = window.confirm('You must be logged in to place an order. Would you like to go to the login page?');
      if (shouldLogin) navigate('/account/signin');
      return;
    }
    
    if (clientSecret || intentLoading) return;

    try {
      setIntentLoading(true);

      const items = cartChargeItems(cart);
      
      const res = await axios.post(`${config.API_BASE}/api/payments/create-intent`, {
        items,
        customerId: user?._id,
      });

      setPriceQuote(quoteForItems(res.data, items));
      setPricingError('');
      setClientSecret(res.data.clientSecret);
      setPaymentIntentId(res.data.paymentIntentId);
    } catch (err) {
      console.error('Failed to create payment intent', err);
      alert('Unable to start payment. Please try again.');
      intentCreatedRef.current = false;
    } finally {
      setIntentLoading(false);
    }
  };

  // PaymentBox Component - OUTSIDE of handleProceedToPayment
  const PaymentBox = ({
    onSuccess,
    savedPaymentMethods,
    defaultPaymentMethodId,
    loadingPaymentMethods,
    isDistributionComplete,
    onValidateDistribution,
  }) => {
    const stripe = useStripe();
    const elements = useElements();
    const { user, token } = useAuth();

    const [submitting, setSubmitting] = useState(false);
    const [cardholderName, setCardholderName] = useState('');
    const [cardComplete, setCardComplete] = useState(false);
    const [expiryComplete, setExpiryComplete] = useState(false);
    const [cvcComplete, setCvcComplete] = useState(false);
    const [cardError, setCardError] = useState('');

    // Saved card selection state
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
    const [useNewCard, setUseNewCard] = useState(false);
    const [saveNewCard, setSaveNewCard] = useState(false);

    // Pre-select default card if available
    useEffect(() => {
      if (!loadingPaymentMethods) {
        if (savedPaymentMethods.length > 0 && defaultPaymentMethodId) {
          setSelectedPaymentMethodId(defaultPaymentMethodId);
          setUseNewCard(false);
        } else {
          setUseNewCard(true);
        }
      }
    }, [savedPaymentMethods, defaultPaymentMethodId, loadingPaymentMethods]);

    const isFormComplete = isDistributionComplete && (useNewCard
      ? (cardComplete && expiryComplete && cvcComplete && cardholderName.trim())
      : selectedPaymentMethodId !== null);

    const handlePay = async () => {
      if (!onValidateDistribution()) {
        return;
      }

      if (!stripe || !elements) {
        alert('Payment system is still loading. Please wait.');
        return;
      }

      setSubmitting(true);

      try {
        let paymentMethodId;

        // Determine which payment method to use
        if (useNewCard) {
          // Create new payment method from card elements
          const cardNumberElement = elements.getElement(CardNumberElement);

          if (!cardNumberElement) {
            alert('Card input not ready. Please refresh the page.');
            setSubmitting(false);
            return;
          }

          console.log('Creating payment method...');
          const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardNumberElement,
            billing_details: {
              name: cardholderName || user?.name || ''
            }
          });

          if (pmError) {
            console.error('Payment method creation error:', pmError);
            alert(pmError.message || 'Please check your card details.');
            setSubmitting(false);
            return;
          }

          console.log('Payment method created successfully:', paymentMethod.id);
          paymentMethodId = paymentMethod.id;

          // IMPORTANT: If user wants to save this card, attach it to customer BEFORE payment
          // Stripe doesn't allow attaching a PaymentMethod after it's been used for payment
          if (saveNewCard && user?._id) {
            try {
              console.log('Saving payment method BEFORE payment (required by Stripe)...');
              const saveResponse = await axios.post(
                `${config.API_BASE}/api/payments/attach-payment-method`,
                { userId: user._id, paymentMethodId },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              paymentMethodId = saveResponse.data.paymentMethodId || paymentMethodId;
              console.log('✓ Payment method attached to customer:', saveResponse.data.customerId);
            } catch (error) {
              console.error('✗ Failed to attach payment method:', error.response?.data || error.message);
              // Continue with payment even if save fails
            }
          }
        } else {
          // Use selected saved payment method
          paymentMethodId = selectedPaymentMethodId;
          console.log('Using saved payment method:', paymentMethodId);
        }

        if (!chargeReady) {
          alert('The order total is still updating. Please wait.');
          setSubmitting(false);
          return;
        }

        const items = cartChargeItems(cart);

        const orderInfoResponse = await resolveCheckoutOrder(paymentIntentId);
        const checkoutOrderId = orderRecordId(orderInfoResponse.data);

        const updated = await axios.post(`${config.API_BASE}/api/payments/update-intent`, {
          items,
          customerId: user?._id,
          orderNumber: orderInfoResponse.data.orderNumber,
          orderId: checkoutOrderId,
          paymentIntentId
        });
        const chargedCents = updated.data?.totalCents;
        if (chargedCents !== chargeCents) {
          setPriceQuote(quoteForItems(updated.data, items));
          alert(`The order total is now $${formatCents(chargedCents)}. Review it, then pay again.`);
          setSubmitting(false);
          return;
        }

        console.log('Confirming payment...');
        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethodId
        });

        if (error) {
          console.error('Payment confirmation error:', error);
          alert(error.message || 'Payment failed. Please try again.');
          setSubmitting(false);
          return;
        }

        if (paymentIntent.status !== 'succeeded' && paymentIntent.status !== 'processing') {
          alert('Payment was not completed. Please try again.');
          setSubmitting(false);
          return;
        }

        if (user?._id && paymentMethodId && (!useNewCard || saveNewCard)) {
          try {
            await axios.post(
              `${config.API_BASE}/api/payment-methods-orders/${paymentMethodId}/set-default`,
              { userId: user._id },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (defaultErr) {
            console.warn('Could not update default payment method:', defaultErr.response?.data || defaultErr.message);
          }
        }

        pendingCheckoutOrderRef.current = null;
        clearCart();
        navigate('/order-confirmation', {
          state: {
            orderNumber: orderInfoResponse.data.orderNumber,
            orderId: checkoutOrderId,
          }
        });
        if (onSuccess) onSuccess();
      } catch (err) {
        console.error('Payment error:', err);
        const message =
          err.response?.data?.error ||
          err.message ||
          'An error occurred during payment. Please try again.';
        alert(message);
        setSubmitting(false);
      }
    };

    return (
      <div>
        {/* Saved Card Selection UI */}
        {loadingPaymentMethods ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            Loading saved cards...
          </div>
        ) : savedPaymentMethods.length > 0 ? (
          <div style={{ marginBottom: 20 }}>
            <h6 style={{ marginBottom: 15 }}>Select Payment Method</h6>

            {/* Saved Cards Section */}
            <div style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '15px', marginBottom: 15 }}>
              <h6 style={{ marginBottom: 10, fontSize: '14px', fontWeight: 'bold' }}>CREDIT & DEBIT CARDS</h6>

              {savedPaymentMethods.map((method) => (
                <div
                  key={method.id}
                  onClick={() => {
                    setSelectedPaymentMethodId(method.id);
                    setUseNewCard(false);
                  }}
                  style={{
                    border: selectedPaymentMethodId === method.id && !useNewCard ? '2px solid #28a745' : '1px solid #ddd',
                    backgroundColor: selectedPaymentMethodId === method.id && !useNewCard ? '#f8fff8' : 'white',
                    borderRadius: '5px',
                    padding: '10px',
                    marginBottom: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      checked={selectedPaymentMethodId === method.id && !useNewCard}
                      onChange={() => {
                        setSelectedPaymentMethodId(method.id);
                        setUseNewCard(false);
                      }}
                      style={{ marginRight: '10px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong style={{ textTransform: 'uppercase' }}>{method.card.brand}</strong>
                          {' '}ending in {method.card.last4}
                          {method.id === defaultPaymentMethodId && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              backgroundColor: '#007bff',
                              color: 'white',
                              borderRadius: '3px',
                              fontSize: '11px',
                              fontWeight: 'bold'
                            }}>
                              DEFAULT
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#666', fontSize: '14px' }}>
                          Expires {method.card.exp_month}/{method.card.exp_year}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add New Card Option */}
              <div
                onClick={() => setUseNewCard(true)}
                style={{
                  border: useNewCard ? '2px solid #28a745' : '1px solid #ddd',
                  backgroundColor: useNewCard ? '#f8fff8' : 'white',
                  borderRadius: '5px',
                  padding: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    checked={useNewCard}
                    onChange={() => setUseNewCard(true)}
                    style={{ marginRight: '10px' }}
                  />
                  <strong>Add a new card</strong>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Show helpful message when no saved cards exist */
          !loadingPaymentMethods && user?._id && (
            <div style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '5px',
              padding: '15px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <i className="fas fa-info-circle" style={{ color: '#17a2b8', marginRight: '10px', marginTop: '2px' }}></i>
                <div>
                  <strong>No saved cards yet</strong>
                  <p style={{ margin: '5px 0 0 0', color: '#6c757d' }}>
                    Check "Save card for future purchases" below to save this card for faster checkout next time.
                  </p>
                </div>
              </div>
            </div>
          )
        )}

        {/* Card Input Fields - Only show when adding new card */}
        {useNewCard && (
          <>
            <div style={{ marginBottom: 15 }}>
              <label>Cardholder Name: <span style={{ color: 'red' }}>*</span></label>
              <input
                type="text"
                value={cardholderName}
                onChange={e => setCardholderName(e.target.value)}
                className="form-control"
                placeholder="Name on card"
                disabled={submitting}
              />
            </div>
        <div style={{ marginBottom: 15 }}>
          <label>Card Number: <span style={{ color: 'red' }}>*</span></label>
          <div className="form-control" style={{ padding: '10px 12px' }}>
            <CardNumberElement 
              options={{ 
                showIcon: true,
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                },
              }}
              onChange={(e) => {
                setCardComplete(e.complete);
                setCardError(e.error ? e.error.message : '');
              }}
            />
          </div>
          {cardError && (
            <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: 5 }}>
              {cardError}
            </div>
          )}
        </div>
        <div className="row">
          <div className="col-6" style={{ marginBottom: 15 }}>
            <label>Expiry Date: <span style={{ color: 'red' }}>*</span></label>
            <div className="form-control" style={{ padding: '10px 12px' }}>
              <CardExpiryElement 
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                  },
                }}
                onChange={(e) => setExpiryComplete(e.complete)}
              />
            </div>
          </div>
          <div className="col-6" style={{ marginBottom: 15 }}>
            <label>CVV: <span style={{ color: 'red' }}>*</span></label>
            <div className="form-control" style={{ padding: '10px 12px' }}>
              <CardCvcElement 
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                  },
                }}
                onChange={(e) => setCvcComplete(e.complete)}
              />
            </div>
          </div>
        </div>

            {/* Save Card Checkbox */}
            <div style={{
              marginBottom: 15,
              padding: '12px',
              backgroundColor: saveNewCard ? '#e7f3ff' : '#f8f9fa',
              border: `1px solid ${saveNewCard ? '#007bff' : '#dee2e6'}`,
              borderRadius: '5px',
              transition: 'all 0.2s'
            }}>
              <div className="form-check" style={{ marginBottom: 0 }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="saveCardCheckbox"
                  checked={saveNewCard}
                  onChange={(e) => setSaveNewCard(e.target.checked)}
                  disabled={submitting}
                  style={{ cursor: 'pointer' }}
                />
                <label
                  className="form-check-label"
                  htmlFor="saveCardCheckbox"
                  style={{ cursor: 'pointer', fontWeight: saveNewCard ? '600' : '400' }}
                >
                  <i className="fas fa-credit-card" style={{ marginRight: '8px', color: '#007bff' }}></i>
                  Save card for future purchases
                </label>
                {saveNewCard && (
                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px', marginLeft: '24px' }}>
                    Your card will be securely saved for faster checkout next time
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <button
          type="button"
          className="boxed-btn"
          onClick={handlePay}
          disabled={submitting || !stripe || !isFormComplete || !chargeReady}
          style={{ 
            width: '100%',
            backgroundColor: submitting || !stripe || !isFormComplete || !chargeReady ? '#6c757d' : '#28a745',
            color: '#fff',
            border: 'none',
            cursor: submitting || !stripe || !isFormComplete || !chargeReady ? 'not-allowed' : 'pointer'
          }}
        >
          {submitting ? 'Processing…' : chargeReady ? `Pay $${formatCents(chargeCents)}` : 'Updating total…'}
        </button>

        {!isFormComplete && !submitting && (
          <div style={{ fontSize: '0.875rem', color: '#6c757d', marginTop: 10, textAlign: 'center' }}>
            {!isDistributionComplete && 'Complete pickup or delivery details above. '}
            {isDistributionComplete && useNewCard ? (
              <>
                {!cardholderName.trim() && 'Enter cardholder name. '}
                {!cardComplete && 'Complete card number. '}
                {!expiryComplete && 'Enter expiry date. '}
                {!cvcComplete && 'Enter CVV. '}
              </>
            ) : isDistributionComplete ? (
              'Please select a payment method.'
            ) : null}
          </div>
        )}

        <div style={{ marginTop: 15, padding: '10px', background: '#e8f5e8', borderRadius: '4px', fontSize: '12px', color: '#2d5a2d' }}>
          <i className="fas fa-shield-alt" style={{ marginRight: '5px' }}></i>
          Your payment information is secure and encrypted
        </div>
      </div>
    );
  };

  if (!cart.length) {
    return (
      <div>
        <SiteNavBar />
        <div className="hero-area hero-bg" style={{ paddingTop: '3%' }}>
          <div className="container">
            <div className="row">
              <div className="col-lg-9 offset-lg-2 text-center">
                <div className="hero-text">
                  <div className="hero-text-tablecell" style={{ padding: '15% 0' }}>
                    <p className="subtitle" style={{ fontSize: '20px' }}>Farmers Marketspace</p>
                    <h1>CART</h1>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
          <div style={{ paddingTop: '130px', paddingBottom: '20px', display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
            <img src={workprogress} alt="work-in-progress" style={{ width: '300px', height: '300px' }} />
            <h2>Your cart is empty</h2>
          </div>
        </div>
        <Footer />
        <CopyRight />
      </div>
    );
  }

  return (
    <div>
      <SiteNavBar />
      <div className="hero-area hero-bg" style={{ paddingTop: '3%' }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-9 offset-lg-2 text-center">
              <div className="hero-text">
                <div className="hero-text-tablecell" style={{ padding: '15% 0' }}>
                  <p className="subtitle" style={{ fontSize: '20px' }}>Farmers Marketspace</p>
                  <h1>CART & CHECKOUT</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container" style={{ marginTop: '40px', marginBottom: '40px' }}>
        <div className="row">
          <div className="col-md-6">
            <div style={{ background: '#f5f5f5', padding: '20px', marginBottom: '20px' }}>
              <h5>Distribution Information</h5>
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setPickupType('Pickup');
                    setLocation('');
                    setAddress('');
                    setDistributionError('');
                  }}
                  style={{ background: pickupType === 'Pickup' ? 'limegreen' : '#eee', color: pickupType === 'Pickup' ? 'white' : 'black', marginRight: 8 }}
                >
                  Pickup
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPickupType('Delivery');
                    setLocation('');
                    setAddress('');
                    setDistributionError('');
                  }}
                  style={{ background: pickupType === 'Delivery' ? 'limegreen' : '#eee', color: pickupType === 'Delivery' ? 'white' : 'black' }}
                >
                  Delivery
                </button>
              </div>
              <div style={{ marginTop: 10 }}>
                <label>Order Date: </label>
                <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>
                  {date ? (() => {
                    const [year, month, day] = date.split('-');
                    return `${month}/${day}/${year}`;
                  })() : ''}
                </span>
                {periodInfo && (
                  <div style={{ marginTop: '10px', padding: '10px', background: '#e8f5e8', borderRadius: '5px', fontSize: '0.9rem' }}>
                    <div><strong>Order Period:</strong> {periodInfo.period}</div>
                    <div><strong>Order Deadline:</strong> {periodInfo.orderBy}</div>
                    <div><strong>Distribution:</strong> {periodInfo.distribution}</div>
                  </div>
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                <label>Location: <span style={{ color: 'red' }}>*</span></label>
                {pickupType === 'Pickup' ? (
                  <select
                    className="form-control"
                    value={location}
                    onChange={(e) => {
                      setLocation(e.target.value);
                      setDistributionError('');
                    }}
                    required
                  >
                    <option value="">Select pickup location</option>
                    {pickupLocations.map(loc => (
                      <option key={loc.name} value={loc.name}>{loc.name}</option>
                    ))}
                  </select>
                ) : (
                  <select
                    className="form-control"
                    value={location}
                    onChange={(e) => {
                      setLocation(e.target.value);
                      setDistributionError('');
                    }}
                    required
                  >
                    <option value="">Select delivery location</option>
                    {deliveryLocations.map(loc => (
                      <option key={loc.name} value={loc.name}>{loc.name}</option>
                    ))}
                  </select>
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                <label>Address: <span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    setDistributionError('');
                  }}
                  className="form-control"
                  readOnly={pickupType === 'Pickup'}
                  placeholder={pickupType === 'Pickup' ? 'Auto-filled from location' : 'Enter delivery address'}
                  required
                />
              </div>
              {distributionError && (
                <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: 10 }}>
                  {distributionError}
                </div>
              )}
            </div>

            <div style={{ background: '#f5f5f5', padding: '20px' }}>
              <h5>Payment Information</h5>
              {!clientSecret || !stripePromise ? (
                <div style={{ marginTop: 15, padding: '10px', background: '#e8f5e8', borderRadius: '4px', fontSize: '12px', color: '#2d5a2d' }}>
                  <i className="fas fa-shield-alt" style={{ marginRight: '5px' }}></i>
                  {user ? 'Preparing secure payment…' : 'Please login to pay.'}
                </div>
              ) : (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <PaymentBox
                    savedPaymentMethods={savedPaymentMethods}
                    defaultPaymentMethodId={defaultPaymentMethodId}
                    loadingPaymentMethods={loadingPaymentMethods}
                    isDistributionComplete={isDistributionComplete}
                    onValidateDistribution={validateDistribution}
                  />
                </Elements>
              )}
            </div>
          </div>

          <div className="col-md-6">
            <div style={{ background: '#f5f5f5', padding: '20px', marginBottom: '20px' }}>
              <h5>Order Summary</h5>
              <div>
                {cart.length} items in your Order
                {chargeReady ? ` - $${formatCents(priceQuote.subtotalCents)}` : ''}
              </div>
              <table style={{ width: '100%', marginTop: 10 }}>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const claimedQuoteLines = new Set();
                    return cart.map((item, idx) => {
                    const available = (item.quantityListed || 0) - (item.quantitySold || 0);
                    const productId = asClientId(item.productId || item._id || item.id);
                    const lineQty = item.quantity || 1;
                    let quotedLine = null;
                    if (quoteIsCurrent && !pricingError && Array.isArray(priceQuote.lines)) {
                      const matchIndex = priceQuote.lines.findIndex((line, lineIndex) => (
                        !claimedQuoteLines.has(lineIndex)
                        && line.productId === productId
                        && Number(line.quantity) === Number(lineQty)
                      ));
                      if (matchIndex >= 0) {
                        claimedQuoteLines.add(matchIndex);
                        quotedLine = priceQuote.lines[matchIndex];
                      }
                    }
                    const unitCost = quotedLine ? quotedLine.unitCost : item.cost;
                    const showLinePrice = !pricingError;
                    return (
                      <tr key={`${item.productId || item._id || item.id}-${idx}`}>
                        <td>{item.title}</td>
                        <td>
                          <button
                            type="button"
                            onClick={() => {
                              if ((item.quantity || 1) <= 1) {
                                if (confirmRemoveFromCartLine(item.title)) {
                                  removeFromCart(item.id);
                                }
                              } else {
                                decrementQuantity(item.id);
                              }
                            }}
                            style={{ border: 'none', background: '#eee', fontWeight: 'bold', width: 28, height: 28, borderRadius: '50%', marginRight: 4 }}
                          >-</button>
                          {item.quantity || 1}
                          <button
                            type="button"
                            onClick={() => incrementQuantity(item.id)}
                            style={{ border: 'none', background: '#eee', fontWeight: 'bold', width: 28, height: 28, borderRadius: '50%', marginLeft: 4 }}
                            disabled={item.quantity >= available}
                          >+</button>
                          <button
                            type="button"
                            onClick={() => {
                              if (confirmRemoveFromCartLine(item.title)) {
                                removeFromCart(item.id);
                              }
                            }}
                            title="Remove item from cart"
                            aria-label="Remove item from cart"
                            style={{
                              boxSizing: 'border-box',
                              width: 28,
                              height: 28,
                              border: '1px solid #495057',
                              background: '#fff',
                              marginLeft: 4,
                              cursor: 'pointer',
                              padding: 0,
                              borderRadius: '50%',
                              color: '#212529',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              verticalAlign: 'middle',
                              flexShrink: 0,
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 448 512"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z" />
                            </svg>
                          </button>
                        </td>
                        <td>{showLinePrice ? `$${Number(unitCost || 0).toFixed(2)}` : '—'}</td>
                        <td>{showLinePrice ? `$${Number((unitCost || 0) * lineQty).toFixed(2)}` : '—'}</td>
                      </tr>
                    );
                  });
                  })()}
                </tbody>
              </table>
              {pricingError ? (
                <div style={{ marginTop: 10, color: '#dc3545' }}>{pricingError}</div>
              ) : chargeReady ? (
                <>
                  <div style={{ marginTop: 10 }}>Subtotal: ${formatCents(priceQuote.subtotalCents)}</div>
                  <div>Pickup Fee: ${formatCents(priceQuote.pickupFeeCents)}</div>
                  <div><strong>Order Total: ${formatCents(chargeCents)}</strong></div>
                </>
              ) : (
                <div style={{ marginTop: 10 }}>Calculating order total…</div>
              )}
              {!user ? (
                <div style={{ marginTop: 20, textAlign: 'center' }}>
                  <div style={{ color: '#666', marginBottom: 10 }}>You must be logged in to place an order</div>
                  <button 
                    style={{ 
                      background: 'orange', 
                      color: 'white', 
                      padding: '10px 20px', 
                      border: 'none', 
                      borderRadius: 4, 
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }} 
                    onClick={() => navigate('/account/signin')}
                  >
                    Login to Continue
                  </button>
                </div>
              ) : (
                !clientSecret && (<></>)
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <CopyRight />
    </div>
  );
};

export default Cart;
